<!-- 商家端 - 角色管理 -->
<template>
  <div>
    <!-- 搜索栏 -->
    <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="68px">
      <el-form-item label="角色名称" prop="roleName">
        <el-input
          v-model="queryParams.roleName"
          class="!w-200px"
          clearable
          placeholder="请输入角色名称"
          @keyup.enter="getList"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="getList"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button plain type="primary" @click="openForm()">
          <Icon class="mr-5px" icon="ep:plus" />新增角色
        </el-button>
      </el-form-item>
    </el-form>

    <!-- 列表 -->
    <el-table v-loading="loading" :data="list" stripe class="mt-12px">
      <el-table-column label="角色名称" min-width="140" prop="roleName" />
      <el-table-column label="角色编码" min-width="140" prop="roleCode" />
      <el-table-column label="说明" min-width="160" prop="roleDesc" />
      <el-table-column label="拥有权限" min-width="320">
        <template #default="{ row }">
          <template v-if="(row.permissions || []).length">
            <el-tag
              v-for="p in (row.permissions || []).slice(0, 5)"
              :key="p"
              class="mr-4px mb-4px"
              size="small"
              :title="p"
            >
              {{ permissionLabel(p) }}
            </el-tag>
            <el-tag v-if="(row.permissions || []).length > 5" size="small" type="info">
              +{{ (row.permissions || []).length - 5 }}
            </el-tag>
          </template>
          <span v-else class="text-secondary">暂无</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="170" prop="createTime" :formatter="dateFormatter" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openForm(row.id)">编辑</el-button>
          <el-button link type="danger" @click="handleDelete(row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />

    <!-- 角色表单弹窗 -->
    <el-dialog v-model="dialogVisible" :title="formTitle" width="700px" @close="resetForm">
      <el-form ref="formRef" :model="formData" :rules="rules" label-width="90px">
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="formData.roleName" placeholder="请输入角色名称" />
        </el-form-item>
        <el-form-item v-if="!formData.id" label="角色编码" prop="roleCode">
          <el-input v-model="formData.roleCode" placeholder="如 SHOP_EDITOR" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="formData.roleDesc" placeholder="请输入角色说明" />
        </el-form-item>
        <el-form-item label="操作权限" prop="permissions">
          <div v-for="group in permissionGroups" :key="group.groupName" class="mb-12px">
            <div class="text-14px font-600 mb-6px">{{ group.groupName }}</div>
            <el-checkbox-group v-model="formData.permissions">
              <el-checkbox
                v-for="item in group.items"
                :key="item.permissionCode"
                :value="item.permissionCode"
                class="mr-24px mb-4px"
              >
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
          <div class="mt-12px">
            <el-button size="small" @click="selectAll">全选</el-button>
            <el-button size="small" @click="formData.permissions = []">清空</el-button>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import * as MerchantRoleApi from '@/api/merchant/role'
import { dateFormatter } from '@/utils/formatTime'

defineOptions({ name: 'MerchantRole' })

type PermissionItem = { permissionCode: string; label: string }
type PermissionGroup = { groupName: string; items: PermissionItem[] }

const message = useMessage()
const loading = ref(false)
const submitting = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const dialogVisible = ref(false)
const formRef = ref()
const permissionGroups = ref<PermissionGroup[]>([])
/** 权限码 → 中文标签（列表展示用） */
const permissionLabelMap = ref<Record<string, string>>({})
const formTitle = computed(() => (formData.id ? '编辑角色' : '新增角色'))

const queryParams = reactive({ pageNo: 1, pageSize: 10, roleName: '' })
const formData = reactive({
  id: undefined as number | undefined,
  roleCode: '',
  roleName: '',
  roleDesc: '',
  sort: 0,
  permissions: [] as string[]
})
const rules = {
  roleName: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
  roleCode: [{ required: true, message: '请输入角色编码', trigger: 'blur' }]
}

function rebuildPermissionLabelMap(groups: PermissionGroup[]) {
  const map: Record<string, string> = {}
  for (const g of groups) {
    for (const item of g.items || []) {
      if (item.permissionCode) {
        map[item.permissionCode] = item.label || item.permissionCode
      }
    }
  }
  permissionLabelMap.value = map
}

function permissionLabel(code: string) {
  return permissionLabelMap.value[code] || code
}

async function ensurePermissionGroups() {
  if (permissionGroups.value.length) return
  const groups = (await MerchantRoleApi.getPermissionList()) || []
  permissionGroups.value = groups
  rebuildPermissionLabelMap(groups)
}

const getList = async () => {
  loading.value = true
  try {
    await ensurePermissionGroups()
    const data = await MerchantRoleApi.getRolePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const openForm = async (id?: number) => {
  resetForm()
  await ensurePermissionGroups()
  if (id) {
    const row = await MerchantRoleApi.getRole(id)
    formData.id = row.id
    formData.roleCode = row.roleCode
    formData.roleName = row.roleName
    formData.roleDesc = row.roleDesc || ''
    formData.sort = row.sort || 0
    formData.permissions = row.permissions || []
  }
  dialogVisible.value = true
}

const resetForm = () => {
  formData.id = undefined
  formData.roleCode = ''
  formData.roleName = ''
  formData.roleDesc = ''
  formData.sort = 0
  formData.permissions = []
  formRef.value?.resetFields()
}

const selectAll = () => {
  formData.permissions = permissionGroups.value.flatMap((g) =>
    (g.items || []).map((i) => i.permissionCode)
  )
}

const handleSubmit = async () => {
  await formRef.value?.validate()
  submitting.value = true
  try {
    if (formData.id) {
      await MerchantRoleApi.updateRole(formData)
      message.success('更新成功')
    } else {
      await MerchantRoleApi.createRole(formData)
      message.success('创建成功')
    }
    dialogVisible.value = false
    getList()
  } finally {
    submitting.value = false
  }
}

const handleDelete = async (id: number) => {
  try {
    await message.confirm('确认删除该角色？')
    await MerchantRoleApi.deleteRole(id)
    message.success('删除成功')
    getList()
  } catch {}
}

onMounted(() => {
  getList()
})
</script>
